import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Field, StatusPill } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

/**
 * The floating IP: what this node's public address actually is, and the guided setup that binds it.
 *
 * The mechanism, because the UI is unreadable without it: a VPS hub owns nothing but a route. A
 * tinc tunnel is built hub↔node, the hub proxy-ARPs the floating address onto its own LAN so the
 * upstream router delivers that traffic to it, and the node puts the address on the tunnel
 * interface and moves its DEFAULT ROUTE into the tunnel. From then on the node's inbound and
 * outbound traffic both use the floating address — which is what lets a node change provider
 * without its public address changing.
 *
 * The consequence to keep in front of the operator: the address is bound BY tinc. No tinc, no IP.
 * Every service that publishes this node — DNS records, mail identity, the peer allowlist, the
 * mesh itself — is then pinned to an address that exists only while this tunnel is up.
 *
 * Nothing here re-implements the procedure. The eight steps are the tested v4 order, declared once
 * server-side in phase3Tinc and returned by the job; this renders what the job reports. The order
 * matters and is not ours to rearrange: tinc_test proves the tunnel carries traffic BEFORE
 * tinc_switch moves the default route into it, and the switch is launched detached because whoever
 * is connected loses their connection the instant the route flips.
 */

type Status = {
  networks?: string;
  active?: string;
  default_route?: string;
  outbound_ip?: string;
  configured_floating_ip?: string;
  [k: string]: unknown;
};

type Step = { name: string; title?: string; status: string; detail?: string };
type Job = { id?: string; status?: string; steps?: Step[] | null };

export function FloatingIP() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [setup, setSetup] = React.useState<{
    floating_ip: string;
    mode: string;
    vps_ip: string;
    vps_password: string;
    vps_ssh_port: string;
    tinc_port: string;
    tinc_invite: string;
  } | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);

  const st = useQuery({
    queryKey: ["floating-ip"],
    queryFn: () => client.apiFetch<Status>("/cluster/floating-ip"),
    refetchInterval: 30000,
  });

  const job = useQuery({
    queryKey: ["floating-ip-job", jobId],
    enabled: !!jobId,
    queryFn: () => client.apiFetch<Job>(`/cluster/floating-ip/${jobId}`),
    // Polled fast because the interesting part is short and the node stops answering on its old
    // address partway through — a slow poll makes the switch look like a hang.
    refetchInterval: 4000,
  });

  const d = st.data;
  const configured = (d?.configured_floating_ip as string) || "";
  const outbound = (d?.outbound_ip as string) || "";
  const bound = !!configured && outbound === configured;

  return (
    <div className="space-y-4">
      <Section
        title="Public address"
        actions={
          <Button
            size="sm"
            variant={configured ? "ghost" : "soft"}
            onClick={() =>
              setSetup({
                floating_ip: configured,
                mode: "automatic",
                vps_ip: "",
                vps_password: "",
                vps_ssh_port: "22",
                tinc_port: "659",
                tinc_invite: "",
              })
            }
          >
            {configured ? "Re-run setup" : "Set up a floating IP"}
          </Button>
        }
      >
        <StateView loading={st.isLoading} error={st.error} errorContext="the floating IP">
          <div className="space-y-2 text-[12.5px] leading-relaxed">
            {!configured ? (
              <p className="text-muted">
                This node has no floating IP. It is reached on the address its provider gave it, and
                that address changes if the node moves.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted">Configured:</span>
                  <span className="font-mono">{configured}</span>
                  {/* The only claim worth making, and it is measured by asking the internet what
                      address this node came from — not by reading the config, which says what was
                      intended and is identical on a node whose tunnel failed to come up. */}
                  {bound ? (
                    <StatusPill tone="ok">bound — traffic leaves as {outbound}</StatusPill>
                  ) : (
                    <StatusPill tone="bad">NOT in force — traffic leaves as {outbound || "unknown"}</StatusPill>
                  )}
                </div>
                {!bound && (
                  <p className="rounded border border-bad/40 bg-bad/10 px-2 py-1.5">
                    Everything that publishes this node — DNS records, mail identity, the peer
                    allowlist — points at {configured}, and this node is not answering on it. The
                    tunnel is the only thing that binds that address: no tinc, no IP.
                  </p>
                )}
                <div>
                  <span className="text-muted">Default route: </span>
                  <span className="font-mono text-[12px]">{d?.default_route || "—"}</span>
                </div>
                <div>
                  <span className="text-muted">Tinc networks: </span>
                  <span className="font-mono text-[12px]">{d?.networks || "none"}</span>
                  {d?.active ? <span className="text-muted"> · active: {d.active}</span> : null}
                </div>
              </>
            )}
          </div>
        </StateView>
      </Section>

      {jobId && (
        <Section title="Setup progress" hint={job.data?.status}>
          <StateView loading={job.isLoading} error={job.error} errorContext="the setup job">
            <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
              This node stops answering on its old address at <span className="font-mono">tinc_switch</span> —
              that is the switch working, not a failure. The job runs inside the panel rather than
              over your connection, so it survives the flip; reconnect on the floating address to
              watch the rest.
            </p>
            <div className="divide-y divide-line rounded-lg border border-line">
              {(job.data?.steps ?? []).map((s) => (
                <div key={s.name} className="flex items-start justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <span className="font-mono text-[12px]">{s.name}</span>
                    {s.detail && <div className="mt-0.5 text-[11.5px] text-muted">{s.detail.slice(0, 160)}</div>}
                  </div>
                  <StatusPill
                    tone={
                      s.status === "done" || s.status === "ok"
                        ? "ok"
                        : s.status === "failed"
                          ? "bad"
                          : s.status === "running"
                            ? "accent"
                            : "neutral"
                    }
                  >
                    {s.status}
                  </StatusPill>
                </div>
              ))}
            </div>
          </StateView>
        </Section>
      )}

      <ConfirmDialog
        open={!!setup}
        onOpenChange={(o) => !o && setSetup(null)}
        title="Bind this node to a floating IP"
        confirmLabel="Start the setup"
        destructive
        body={
          <div className="space-y-2">
            <Field mono label="Floating IP" value={setup?.floating_ip ?? ""} placeholder="116.202.188.88"
              onChange={(v) => setSetup(setup ? { ...setup, floating_ip: v } : null)}
              help="The portable public address this node will own. It must already be routed to the hub by your provider." />
            <Field mono label="Hub address" value={setup?.vps_ip ?? ""} placeholder="5.161.59.247"
              onChange={(v) => setSetup(setup ? { ...setup, vps_ip: v } : null)}
              help="The VPS that owns the route and proxy-ARPs the floating address. It needs nothing but Debian and reachable SSH — the panel installs its own tinc 1.1 there if the distro's is too old." />
            <Field mono label="Hub root password" type="password" value={setup?.vps_password ?? ""}
              onChange={(v) => setSetup(setup ? { ...setup, vps_password: v } : null)}
              help="Held in memory for the life of this job and never written to the database. A panel restart therefore fails the job rather than persisting it." />
            <div className="grid gap-2 sm:grid-cols-2">
              <Field mono label="Hub SSH port" value={setup?.vps_ssh_port ?? "22"}
                onChange={(v) => setSetup(setup ? { ...setup, vps_ssh_port: v } : null)} />
              <Field mono label="Tunnel port" value={setup?.tinc_port ?? "659"}
                onChange={(v) => setSetup(setup ? { ...setup, tinc_port: v } : null)}
                help="One port per node on the hub — 659, 660, 661… A second node reusing a port collides with the first." />
            </div>
            <p className="rounded border border-warn/40 bg-warn/10 px-2 py-1.5 text-[12px] leading-relaxed">
              The tunnel is tested before anything moves. Only after it is proven to carry traffic
              does the default route switch — and at that moment this node stops answering on its
              current address. Everything that names this node afterwards must name{" "}
              <span className="font-mono">{setup?.floating_ip || "the floating IP"}</span>.
            </p>
          </div>
        }
        onConfirm={() => {
          const s = setup;
          setSetup(null);
          if (!s?.floating_ip.trim()) return;
          void client
            .apiFetch<{ job_id?: string }>("/cluster/floating-ip", {
              method: "POST",
              body: JSON.stringify({
                floating_ip: s.floating_ip.trim(),
                mode: s.tinc_invite.trim() ? "manual" : "automatic",
                vps_ip: s.vps_ip.trim(),
                vps_password: s.vps_password,
                vps_ssh_port: s.vps_ssh_port.trim() || "22",
                tinc_port: s.tinc_port.trim() || "659",
                ...(s.tinc_invite.trim() ? { tinc_invite: s.tinc_invite.trim() } : {}),
              }),
            })
            .then((r) => {
              setJobId(r?.job_id ?? null);
              notify({ kind: "success", title: "Floating-IP setup started", body: r?.job_id });
              void qc.invalidateQueries({ queryKey: ["floating-ip"] });
            })
            .catch((e: unknown) =>
              notify({
                kind: "error",
                title: "Could not start the setup",
                body: e instanceof Error ? e.message : String(e),
              }),
            );
        }}
      />
    </div>
  );
}
